import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';
import { buildImageUrl } from '../../utils/imageUrl';
import { buildFinishSheetHtml } from './finishSheetPrint';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Save, Send, Printer, Image as ImageIcon, X, Lock,
  Unlock, CheckCircle2, Loader2, Paintbrush, Scissors, Wrench
} from 'lucide-react';

const TYPE_META = {
  paint:  { label: 'Paint Finish',  icon: Paintbrush },
  fabric: { label: 'Fabric Finish', icon: Scissors },
  metal:  { label: 'Metal Finish',  icon: Wrench },
};
const MATERIAL_OPTIONS = [
  { value: '', label: '— Select —' },
  { value: 'sheet', label: 'Sheet' },
  { value: 'pipe', label: 'Pipe' },
  { value: 'pipe_material', label: 'Pipe Material' },
  { value: 'other', label: 'Other (add item)' },
];

export default function FinishSheetPage() {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/worker') ? '/worker' : '/admin';
  const { canWrite, canEdit } = usePermission('finish');
  const { user } = useAuth();
  const { settings } = useSettings() || {};
  const meta = TYPE_META[type] || TYPE_META.paint;
  const TypeIcon = meta.icon;

  const [data, setData] = useState(null);
  const [entries, setEntries] = useState({}); // keyed by main_po_item_id ("<id>:without" = the rest without this finish)
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(null); // `${entryKey}:${imageType}`
  const fileInputRef = useRef(null);
  const uploadCtx = useRef(null);

  const isSubmitted = data?.sheet?.status === 'submitted';
  const editable = !isSubmitted && canWrite;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/main-pos/${id}/finish/${type}`);
      setData(res.data);
      setRemarks(res.data.sheet?.remarks || '');
      const map = {};
      res.data.rows.forEach(r => {
        map[r.item.id] = r.entry ? { ...r.entry } : {};
        if (r.item.rest_qty > 0) map[`${r.item.id}:without`] = r.rest_entry ? { ...r.rest_entry } : {};
      });
      setEntries(map);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load finish sheet');
      navigate(`${basePath}/finish`);
    } finally {
      setLoading(false);
    }
  }, [id, type, navigate, basePath]);

  useEffect(() => { load(); }, [load]);

  const setEntry = (itemId, patch) =>
    setEntries(m => ({ ...m, [itemId]: { ...(m[itemId] || {}), ...patch } }));

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const payload = {
        remarks,
        entries: Object.entries(entries).map(([key, e]) => {
          const [itemId, part] = key.split(':');
          return { ...e, main_po_item_id: Number(itemId), part: part === 'without' ? 'without' : 'with' };
        }),
      };
      await api.put(`/main-pos/${id}/finish/${type}`, payload);
      if (!silent) toast.success('Finish sheet saved');
      await load();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!window.confirm(`Submit this ${meta.label} sheet? It will be locked after submission (an admin can reopen it).`)) return;
    setSubmitting(true);
    try {
      const ok = await save(true); // persist latest edits first
      if (!ok) return;
      await api.post(`/main-pos/${id}/finish/${type}/submit`);
      toast.success('Sheet submitted and locked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const reopen = async () => {
    if (!window.confirm('Reopen this sheet for editing?')) return;
    try {
      await api.post(`/main-pos/${id}/finish/${type}/reopen`);
      toast.success('Sheet reopened');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reopen failed');
    }
  };

  // ── images ─────────────────────────────────────────────────────────────────
  const pickImages = (saved, key, imageType) => {
    if (!saved?.id) return toast.error('Save the sheet first, then upload images to this row');
    uploadCtx.current = { entryId: saved.id, key, imageType };
    fileInputRef.current?.click();
  };

  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const ctx = uploadCtx.current;
    if (!files.length || !ctx) return;
    setUploading(`${ctx.key}:${ctx.imageType}`);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('images', f));
      fd.append('image_type', ctx.imageType);
      await api.post(`/finish-entries/${ctx.entryId}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Image(s) uploaded');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const deleteImage = async (img) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/finish-entry-images/${img.id}`);
      toast.success('Image deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  // ── print: MOZI template (finishSheetPrint.js); fabric prints landscape ────
  const handlePrint = () => {
    if (!data) return;
    const win = window.open('', '_blank');   // open straight from the click so pop-up blockers allow it
    if (!win) return toast.error('Allow pop-ups to print');
    win.document.write(buildFinishSheetHtml(data, type, entries, remarks, settings || {}, window.location.origin));
    win.document.close();
    win.focus();
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Loading…</div>;
  if (!data) return null;

  const { po, rows } = data;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onFilesChosen} />

      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`${basePath}/finish`)} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2">
              <TypeIcon className="w-5 h-5 text-blue-600" /> {meta.label} — {po.po_no}
            </h1>
            <p className="text-sm text-gray-500">{po.project_name}{po.linked_project_code ? ` · ${po.linked_project_code}` : ''}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handlePrint}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-semibold text-gray-700">
            <Printer className="w-4 h-4" /> Print{type === 'fabric' ? ' (Landscape)' : ''}
          </button>
          {editable && (
            <>
              <button onClick={() => save()} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
              <button onClick={submit} disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit &amp; Lock
              </button>
            </>
          )}
          {isSubmitted && user?.role === 'admin' && (
            <button onClick={reopen}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-sm font-semibold">
              <Unlock className="w-4 h-4" /> Reopen
            </button>
          )}
        </div>
      </div>

      {isSubmitted && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4" /> This sheet is submitted and locked.
        </div>
      )}
      {rows.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
          No items on this Manufacturing Order are flagged for {meta.label}. Set the flag to YES on the Manufacturing Order item rows.
        </div>
      )}

      {/* Rows */}
      {rows.map((r, idx) => {
        const fq = Number(r.item.finish_qty ?? r.item.qty);
        const rest = Number(r.item.rest_qty) || 0;
        const word = meta.label.split(' ')[0].toLowerCase();         // paint / fabric
        const partProps = { type, editable, canEdit, uploading, setEntry, pickImages, deleteImage, meta };
        return (
          <div key={r.item.id} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            {/* Read-only base data from Manufacturing Order item */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
              <span className="text-xs font-bold text-gray-400">ROW {idx + 1}</span>
              <span className="font-bold text-gray-800">{r.item.product_name}</span>
              {r.item.proto_code && <span className="text-blue-700 font-semibold">{r.item.proto_code}</span>}
              {r.item.area && <span className="text-gray-500">Area: {r.item.area}</span>}
              {rest > 0
                ? <span className="text-gray-700">{meta.label} Qty: <strong>{fq}</strong> <span className="text-gray-400">of {Number(r.item.qty)} ({rest} without)</span></span>
                : <span className="text-gray-700">Qty: <strong>{Number(r.item.qty)}</strong></span>}
              <span className="text-xs text-gray-400 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> from Manufacturing Order</span>
            </div>
            {(r.item.ref_images || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {r.item.ref_images.map(img => (
                  <a key={img.id} href={buildImageUrl(img.image_path)} target="_blank" rel="noreferrer">
                    <img src={buildImageUrl(img.image_path)} alt="ref" className="w-14 h-14 object-cover rounded border border-gray-200" />
                  </a>
                ))}
              </div>
            )}

            {rest > 0 ? (
              <div className="grid gap-3 lg:grid-cols-2">
                <FinishPart {...partProps} title={`1 · With ${word}`} qty={fq} tone="with"
                  entryKey={String(r.item.id)} e={entries[r.item.id] || {}} saved={r.entry} />
                <FinishPart {...partProps} title={`2 · Without ${word}`} qty={rest} tone="without"
                  entryKey={`${r.item.id}:without`} e={entries[`${r.item.id}:without`] || {}} saved={r.rest_entry} />
              </div>
            ) : (
              <FinishPart {...partProps} qty={fq} entryKey={String(r.item.id)} e={entries[r.item.id] || {}} saved={r.entry} />
            )}
          </div>
        );
      })}

      {/* Sheet remarks */}
      {rows.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <Field label="Sheet Remarks">
            <input className="inp" disabled={!editable} value={remarks}
              onChange={ev => setRemarks(ev.target.value)} placeholder="Overall remarks for this finish sheet" />
          </Field>
        </div>
      )}

      <style>{`
        .inp { width:100%; padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; font-size:13px; background:#fff; }
        .inp:disabled { background:#f9fafb; color:#4b5563; }
        .inp:focus { outline:2px solid #bfdbfe; border-color:#3b82f6; }
      `}</style>
    </div>
  );
}

// Finish details for one part of an item: the qty WITH this finish, or the rest WITHOUT it
function FinishPart({ type, title, qty, tone, entryKey, e, saved, editable, canEdit, uploading, setEntry, pickImages, deleteImage, meta }) {
  const set = patch => setEntry(entryKey, patch);
  const without = tone === 'without';
  const totalQty = type === 'fabric' && e.per_item_fabric_qty != null && e.per_item_fabric_qty !== ''
    ? (Number(qty) * Number(e.per_item_fabric_qty || 0)).toFixed(2) : null;
  const body = (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {type === 'paint' && (
          <Field label={without ? 'Finish Name (without paint)' : 'Paint Finish Name'} className="col-span-2 md:col-span-4">
            <input className="inp" disabled={!editable} value={e.paint_finish_name || ''}
              onChange={ev => set({ paint_finish_name: ev.target.value })}
              placeholder={without ? 'e.g. Natural / Clear polish only' : 'e.g. PU Matt — Walnut'} />
          </Field>
        )}
        {type === 'fabric' && (
          <>
            <Field label={without ? 'Material / Finish (without fabric)' : 'Fabric Name'} className="col-span-2">
              <input className="inp" disabled={!editable} value={e.fabric_name || ''}
                onChange={ev => set({ fabric_name: ev.target.value })}
                placeholder={without ? 'e.g. Leatherette / Plain wood' : 'e.g. D&D Velvet — Teal'} />
            </Field>
            <Field label={`Per Item Qty (${e.fabric_unit || 'MTR'})`}>
              <input className="inp" type="number" min="0" step="0.01" disabled={!editable}
                value={e.per_item_fabric_qty ?? ''}
                onChange={ev => set({ per_item_fabric_qty: ev.target.value })} />
            </Field>
            <Field label="Total Qty (auto)">
              <input className="inp bg-blue-50 font-bold" disabled
                value={totalQty != null ? `${totalQty} ${e.fabric_unit || 'MTR'}` : '—'} />
            </Field>
          </>
        )}
        {type === 'metal' && (
          <>
            <Field label="Material">
              <select className="inp" disabled={!editable} value={e.material_type || ''}
                onChange={ev => set({ material_type: ev.target.value })}>
                {MATERIAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            {e.material_type === 'other' && (
              <Field label="Other Item">
                <input className="inp" disabled={!editable} value={e.material_other || ''}
                  onChange={ev => set({ material_other: ev.target.value })} />
              </Field>
            )}
            <Field label="Metal Finish Details" className="col-span-2">
              <input className="inp" disabled={!editable} value={e.metal_finish_details || ''}
                onChange={ev => set({ metal_finish_details: ev.target.value })}
                placeholder="Add details" />
            </Field>
            <Field label="Length"><input className="inp" disabled={!editable} value={e.size_length || ''} onChange={ev => set({ size_length: ev.target.value })} /></Field>
            <Field label="Width"><input className="inp" disabled={!editable} value={e.size_width || ''} onChange={ev => set({ size_width: ev.target.value })} /></Field>
            <Field label="Thickness"><input className="inp" disabled={!editable} value={e.size_thickness || ''} onChange={ev => set({ size_thickness: ev.target.value })} /></Field>
            <Field label="Other (size)"><input className="inp" disabled={!editable} value={e.size_other || ''} onChange={ev => set({ size_other: ev.target.value })} /></Field>
          </>
        )}
        <Field label="Remark" className="col-span-2 md:col-span-4">
          <input className="inp" disabled={!editable} value={e.remark || ''}
            onChange={ev => set({ remark: ev.target.value })} placeholder="Row remark" />
        </Field>
      </div>

      <div className="flex flex-wrap gap-6 pt-1">
        <ImageGroup
          label={without ? 'Images' : type === 'paint' ? 'Paint Finish Images' : type === 'fabric' ? 'Fabric Images' : 'Metal Images'}
          images={saved?.images || []}
          canUpload={editable} uploading={uploading === `${entryKey}:finish`}
          onUpload={() => pickImages(saved, entryKey, 'finish')}
          onDelete={canEdit && editable ? deleteImage : null}
          needsSave={!saved?.id}
        />
        {type === 'fabric' && (
          <ImageGroup
            label={without ? 'Sample' : 'Fabric Sample'} highlight
            images={saved?.sample_images || []}
            canUpload={editable} uploading={uploading === `${entryKey}:sample`}
            onUpload={() => pickImages(saved, entryKey, 'sample')}
            onDelete={canEdit && editable ? deleteImage : null}
            needsSave={!saved?.id}
          />
        )}
      </div>
    </>
  );
  if (!title) return <div className="space-y-3 pt-1 border-t border-gray-100">{body}</div>;
  return (
    <div className={`rounded-lg border p-3 space-y-3 ${without ? 'border-gray-200 bg-gray-50/60' : 'border-teal-200 bg-teal-50/40'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-gray-700">{title}</span>
        <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-xs font-bold text-gray-700">Qty {qty}</span>
      </div>
      {body}
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ImageGroup({ label, images, canUpload, uploading, onUpload, onDelete, needsSave, highlight }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`text-xs font-semibold ${highlight ? 'text-blue-700' : 'text-gray-500'}`}>{label} ({images.length})</span>
        {canUpload && (
          <button onClick={onUpload} disabled={uploading}
            className="flex items-center gap-1 px-2 py-0.5 rounded-lg border border-gray-300 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
            {needsSave ? 'Save first' : 'Upload'}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {images.map(img => (
          <div key={img.id} className="relative group">
            <a href={buildImageUrl(img.image_path)} target="_blank" rel="noreferrer">
              <img src={buildImageUrl(img.image_path)} alt={label}
                className={`w-16 h-16 object-cover rounded-lg border ${highlight ? 'border-blue-300' : 'border-gray-200'}`} />
            </a>
            {onDelete && (
              <button onClick={() => onDelete(img)}
                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        {images.length === 0 && <span className="text-xs text-gray-300">No images</span>}
      </div>
    </div>
  );
}
